import { z } from "zod";

/**
 * The wire contract for the activity feed.
 *
 * ==========================================================================================
 * `eventType` AND `entityType` ARE STRINGS HERE, NOT ENUMS, AND THAT IS THE POINT
 * ==========================================================================================
 *
 * There are 55 event types and 12 entity types, and this file names none of them. `domain.md` §7 lists
 * activity event names among the things that do NOT go in the shared kernel: a union of every context's
 * event names would couple all six writers through one type that changes whenever any of them adds an
 * event. A `z.enum([...])` here would be exactly that union, arriving through the back door.
 *
 * So the kernel validates the SHAPE of a page and the `audit` context owns the VOCABULARY — its
 * catalogue is a total map over its own copy of the two unions, and a test in `apps/api` proves that copy
 * equals the database enums in both directions. See research R8 and R9.
 *
 * ==========================================================================================
 * THERE IS NO `meta` FIELD
 * ==========================================================================================
 *
 * `message` is already the finished sentence, composed at write time. A `meta` field would be one the
 * reader does not render, which is how `ExpensesPageDtoSchema` drifted into fiction — and it is the one
 * field that can arrive in two shapes, some pre-fix rows holding a jsonb string rather than an object.
 *
 * ==========================================================================================
 * THERE IS NO `total`
 * ==========================================================================================
 *
 * Counting the household's entries would be a second query for a number the interface never shows. The
 * end of the log is signalled by `nextCursor: null`, not by a count, and not by a short page — a filtered
 * page can be short with more behind it.
 */

export const ActivityEntryDtoSchema = z.strictObject({
  id: z.string(),
  /** ISO 8601, UTC. Formatted at the edge, never on the way in. */
  occurredAt: z.string(),
  /** The name the actor had when this happened. A snapshot, never a live lookup. */
  actorName: z.string(),
  /** True when the entry has no actor: automated work, or an actor who deleted their account. */
  isSystem: z.boolean(),
  eventType: z.string(),
  status: z.enum(["success", "error"]),
  message: z.string(),
  entityType: z.string(),
  /** Absent for an entry that names no single record. Such a row is not activatable. */
  entityId: z.string().nullable(),
});

export const ActivityFeedPageDtoSchema = z.strictObject({
  entries: z.array(ActivityEntryDtoSchema),
  /** Opaque, and only ever a value this endpoint issued. `null` means the log has been read to its end. */
  nextCursor: z.string().nullable(),
});

export type ActivityEntryDto = z.infer<typeof ActivityEntryDtoSchema>;
export type ActivityFeedPageDto = z.infer<typeof ActivityFeedPageDtoSchema>;

/**
 * The feed's query string.
 *
 * `hideSystem` arrives as the string "true" or "false" — it is a query parameter, and JSON has not been
 * involved. Coercing here rather than in the route keeps the one place a caller can lie about the shape
 * of a request in the same file as the shape itself.
 *
 * PAGE SIZE IS NOT A PARAMETER. AC-AL7 fixes it at 50, and a client-supplied limit on a table with no
 * upper bound is a denial-of-service knob.
 */
export const ActivityFeedQuerySchema = z.strictObject({
  cursor: z.string().min(1).optional(),
  hideSystem: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
});

export type ActivityFeedQuery = z.infer<typeof ActivityFeedQuerySchema>;
